/**
 * Usage:
 * <product-modal-toggle 
 *   data-product-url="/products/example" 
 *   data-section-id="product-modal-section"
 *   data-product-title="Example Product">
 *   data-tracking-id="product-modal-section || {{ section.id }}"
 *   data-type="product"
 * </product-modal-toggle>
*/
class ProductModal extends HTMLElement {
    constructor() {
        super();
        this.CACHE = new Map();
        this.CACHE_LIMIT = 10;
        
        // elements
        this.content = this.querySelector('pmd-content');
        this.close = this.querySelector('pmd-close');
        this.loader = this.querySelector('pmd-loader');

        // events
        this.addEventListener('click', (e) => {
            if (e.target.classList.contains('active')) {
              this.hide()
            }
        })

        this.close.addEventListener('click', () => {
            this.hide();
        });
    }

    init(toggleElement) {
        this.toggleElement = toggleElement;
        if (toggleElement.tagName.toLowerCase() == 'inner-product-modal-toggle') {
            this.innerToggle = toggleElement;
        }
        this.url = toggleElement.dataset.productUrl || this.url;
        this.section = this.toggleElement.dataset.sectionId || this.section;
        this.trackingId = this.toggleElement.dataset.trackingId || this.trackingId;
        this.align = this.toggleElement.dataset.align || null;
        this.title = this.toggleElement.dataset.productTitle || this.title;
        this.url = new URL(this.url, window.location.origin);
        this.url.searchParams.set('sections', this.section);
        this.availableVariants = false;
        if(this.toggleElement.hasAttribute('data-js-available-variants')) {
            this.availableVariants = true;
        }

        this.formType = this.toggleElement.tagName.toLowerCase() == 'set-modal-toggle' ? 'add-to-set' : 'add-to-cart';   
        this.show();
    }
    
    async fetchProduct(innerProduct = false) {
        this.activeProduct = this.querySelector('[data-js-product-template].active');
        if (this.activeProduct) {
            this.currentVariant = this.activeProduct.querySelector('product-variants').currentVariant;
        }
        
        if (this.CACHE && this.CACHE.has(this.url.href)) {
            const content = this.CACHE.get(this.url.href);
            const parsedContent = new DOMParser().parseFromString(JSON.parse(content)[this.section], 'text/html');
            this.content.innerHTML = parsedContent.querySelector('.shopify-section').innerHTML;

            setTimeout(() => {
                this.loaded();
                this.initForms();
                this.initFormInputs();
                this.initVariantPicker();
                this.ajaxifyProducts();
                this.initGallery();

                if (this.innerToggle) {
                    this.onInnerProductChange(this.innerToggle);
                }

                if (this.availableVariants  == true) {
                    this.filterAvailableVariants();
                }
            }, 0);

        } else {
            try {
                const response = await fetch(this.url, {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                });
                const content = await response.text();

                if (response.ok && content) {
                    const parsedContent = new DOMParser().parseFromString(JSON.parse(content)[this.section], 'text/html');
                    this.content.innerHTML = parsedContent.querySelector('.shopify-section').innerHTML;
                    this.initForms();
                    this.initFormInputs();
                    this.initVariantPicker();
                    this.ajaxifyProducts();
                    if (innerProduct) {
                        this.onInnerProductChange(this.innerToggle); 
                    }

                    if( this.availableVariants == true ) {
                        this.filterAvailableVariants();
                    }

                    if (this.CACHE.size > this.CACHE_LIMIT) {
                        const oldestKey = this.CACHE.keys().next().value;
                        this.CACHE.delete(oldestKey);
                    }
                    
                    this.CACHE.set(this.url.href, content);

                } else {
                    this.error(response.statusText);
                }
            } catch (error) {
                this.error(error);
            } finally {
                this.loaded();
                this.initGallery();

            }
        }
    }

    filterAvailableVariants() {
        const products = this.querySelectorAll('[data-js-product-template]');
        const currentProduct = this.querySelector('[data-js-product-template].active');
        
        if (!products?.length || !currentProduct) return;
        
        const currentVariant = currentProduct.querySelector('product-variants')?.currentVariant;
        if (!currentVariant) return;
    
        const elementsToRemove = new Set();
        
        // Option matching functions
        const optionMatchers = {
            1: (variant, current) => variant.option2 === current.option2 && variant.option3 === current.option3,
            2: (variant, current) => variant.option1 === current.option1 && variant.option3 === current.option3,
            3: (variant, current) => variant.option1 === current.option1 && variant.option2 === current.option2
        };
    
        const optionGetters = {
            1: variant => variant.option1,
            2: variant => variant.option2,
            3: variant => variant.option3
        };
    
        products.forEach(product => {
            const jsonElement = product.querySelector('[type="application/json"]');
            if (!jsonElement) return;
            
            let variantData;
            try {
                variantData = JSON.parse(jsonElement.textContent);
            } catch (e) {
                return;
            }
    
            const colorPosition = parseInt(product.dataset.colorPosition);
            const matcher = optionMatchers[colorPosition];
            const optionGetter = optionGetters[colorPosition];
            
            if (!matcher || !optionGetter) return;
    
            // Find unavailable variants
            const unavailableVariants = variantData.filter(variant => 
                !variant.available && matcher(variant, currentVariant)
            );
    
            // Get option values to remove
            const optionValuesToRemove = new Set(
                unavailableVariants.map(optionGetter)
            );
    
            // Find elements to remove
            optionValuesToRemove.forEach(optionValue => {
                if (optionValue) {
                    const escapedValue = optionValue.replace(/"/g, '\\"');
                    const selector = `.product-variant__item a[data-value="${escapedValue}"], .product-variant__item input[value="${optionValue}"]`;
                    
                    document.querySelectorAll(selector).forEach(element => {
                        if (element.parentElement) {
                            elementsToRemove.add(element.parentElement);
                        }
                    });
                }
            });
        });
    
        // Remove all elements at once
        elementsToRemove.forEach(element => element.remove());
    }

    ajaxifyProducts() {
        this.querySelectorAll('[data-js-merged-option]').forEach(el => {
            el.addEventListener('click', (event) => {
                event.preventDefault();
                this.onProductChange(event);
            });
        });


    }

    handleAddToSet(e) {
        e.preventDefault();
        const card = this.toggleElement.closest('.product-item'); 
        const product = e.target.closest('[data-js-product-template]');
        const productForm = e.target.closest('form');
        const productVariants = product.querySelector('product-variants');
        const productCurrentVariant = productVariants.currentVariant;
        

        // Update card form values
        card.querySelector('input[name="product-id"]').value = productForm.querySelector('input[name="product-id"]').value;
        card.querySelector('input[name="properties[_modal_attribution_url]"]').value = window.location.pathname;
        card.querySelector('input[name="properties[_modal_attribution_section_id]"]').value = this.trackingId;
        const cardVariantId = card.querySelector('input[name="id"]');
        cardVariantId.value = productForm.querySelector('input[name="id"]').value;
        cardVariantId.setAttribute('data-price', productCurrentVariant.price);
        cardVariantId.setAttribute('data-compare-at-price', productCurrentVariant.compare_at_price);

        // Update toggle element and card information
        this.toggleElement.setAttribute('data-variant-id', cardVariantId.value);
        this.toggleElement.setAttribute('data-product-handle', product.dataset.href);
        card.querySelector('.product-item__title-set').textContent = product.dataset.title;

        // Update product variant title
        const productOptionsArray = Array.from(productVariants.querySelectorAll('[data-js-current-option-label]')).map(option => option.textContent);
        const productVariantTitle = productOptionsArray.join(' / ');
        card.querySelector('[data-js-selected-variant]').textContent = productVariantTitle;

        // Update product images
        const modalProductImage = product.querySelector(`.product-gallery-item[data-media-id="${productCurrentVariant.featured_media.id}"] img`) || product.querySelectorAll('.product-gallery-item img')[0];
        const productImage = card.querySelector('.product-item__image img');
        productImage.style.opacity = 0;
        productImage.src = modalProductImage.getAttribute('src');
        productImage.srcset = modalProductImage.getAttribute('srcset');
        productImage.onload = () => {
            productImage.style.opacity = 1;
            card.querySelector('.product-item__image figure').classList.add('clip-reveal');
            setTimeout(() => {
                card.querySelector('.product-item__image figure').classList.remove('clip-reveal');
            }, 300);
        };

        // Initialize product set if available
        if (this.productSet) {
            this.productSet.initSetForm();
            this.productSet.checkSet();
        }

        this.hide();
    }

    keepOptions(decoded_variant, newProduct) {
        const productVariants = newProduct.querySelector('product-variants');
        if (!productVariants) return;
    
    const historicalVariant = productVariants.getVariantData().find(variant => 
        variant.options.every((opt, i) => opt.toLowerCase() === decoded_variant[i].toLowerCase())
    );
    
    if (historicalVariant) {
        const variantContainers = productVariants.querySelectorAll('[data-js-product-variant]');
        
        historicalVariant.options.forEach((option, i) => {
        const container = variantContainers[i]?.querySelector('[data-js-product-variant-container]');
        if (!container) return;

        if (container.dataset.jsProductVariantContainer === 'radio') {
            const input = container.querySelector(`input[value="${option.replace(/"/g, '\\"')}"]`);
            if (input) input.checked = true;
        }
        });
        productVariants.active ? productVariants.onVariantChange() : this.initVariantPicker();
    }
    }

    onProductChange(event) {
        const currentProduct = this.querySelector('[data-js-product-template].active');
        const newHandle = event.target.dataset.href;
        const newProduct = this.querySelector(`[data-js-product-template][data-href="${newHandle}"]`);
        const decoded_variant = JSON.parse(decodeURIComponent(event.target.dataset.currentOptions.replace(/\+/g, ' ')));

        currentProduct.classList.remove('active');
        newProduct.classList.add('active');

        this.initVariantPicker();
        this.keepOptions(decoded_variant, newProduct);
        this.updateLabels();
    }

    onInnerProductChange(innerToggle) {
        const newHandle = innerToggle.dataset.productUrl;
        const newProduct = this.querySelector(`[data-js-product-template][data-href="${newHandle}"]`);
        const decoded_variant = JSON.parse(decodeURIComponent(innerToggle.dataset.currentOptions.replace(/\+/g, ' ')));
        this.innerToggle = null;
        this.initVariantPicker();
        this.keepOptions(decoded_variant, newProduct);
        this.updateLabels();
    }


    initGallery(template) {

        if (!template) {
            template = this.querySelector('[data-js-product-template].active');
        }

        const slider = template.querySelector('css-slider');
        const isActive = slider.classList.contains('enabled');

        if(slider && ! isActive ) {
            slider.initSlider();
            slider.changeSlide(0);
        } else {
            slider.resetSlider();
        }

        template.querySelector('product-variants').onVariantChange();
    }

    updateLabels() {
        const variants = this.querySelector('[data-js-product-template].active').querySelectorAll('[data-js-product-variant]');
        variants.forEach(variant => {
            const option = variant.querySelector('input:checked');
            const label = variant.querySelector('.product-variant__item-text-label');
            label.textContent = option.value;
        });
    }

    onVariantChange(e) {
        const currentVariant = e.target.currentVariant; 
        const currentVariantFeaturedMedia = currentVariant.featured_media.id;
        const gallery = e.target.closest('.product-modal--content').querySelector('css-slider');

        const price = e.target.closest('.product-modal--content').querySelector('[final-price]');

        if(e.target.hasAttribute('data-show-free-wording')) {
            price.innerHTML = `<span style="color: var(--color-sale);">FREE</span>`
        } else {
            price.innerHTML = `<span class="igPrice">${window._formatMoney(currentVariant.price, KROWN.settings.shop_money_format)}</span>`;
        }

        this.updateLabels();

        const currentVariantTitle = e.target.closest('.product-modal--content').querySelector('[data-js-current-variant-title]');
        if(currentVariantTitle) {
            const options = [currentVariant.option1, currentVariant.option2, currentVariant.option3].filter(Boolean);
            currentVariantTitle.textContent = options.join(' / ');
        }

        if (!gallery) {
            console.error('Gallery not found');
            return;
        }

        const currentVariantImage = gallery.querySelector(`.product-gallery-item[data-media-id="${currentVariantFeaturedMedia}"]`);

        if (gallery) {
            if (gallery.sliderEnabled) {
                gallery.changeSlide(currentVariantImage.dataset.index);
            } else {
                gallery.scrollTo({
                    left: currentVariantImage.offsetLeft,
                    behavior: 'auto'
                });
            }
        }
    }

    initVariantPicker() {
        const variantSelector = this.querySelector('[data-js-product-template].active').querySelector('product-variants');
        if (variantSelector && !variantSelector.active) {
            variantSelector.init();
            variantSelector.addEventListener('VARIANT_CHANGE', (e) => {
                this.onVariantChange(e);
            });
        }
    }

    initForms(){
        const forms = this.querySelectorAll('product-form');
        forms.forEach(form => {
            form.addEventListener('add-to-cart', ()=>{
                document.getElementById('site-cart-sidebar').show();
                this.hide();
                if ( document.getElementById('cart-recommendations-sidebar') ) {
                    document.getElementById('cart-recommendations-sidebar').classList.add('loading');
                    document.getElementById('cart-recommendations-sidebar').generateRecommendations();
                }
            });
        });
    }

    initFormInputs() {
        const forms = this.querySelectorAll('form');

        if(forms) {
            forms.forEach(form => {
                const templates = form.querySelectorAll('template');

                templates.forEach(template => {
                    const clone = template.content.cloneNode(true);
                    if(template.getAttribute('id') == 'variant_id') {
                        return; // We don't need to clone the variant_id template
                    } else if(template.getAttribute('id') == 'attribution_form') {
                        const inputElement = clone.querySelector('input');
                        if (inputElement) {
                            inputElement.setAttribute('value', `${this.formType}`); 

                        }
                    } else if(template.getAttribute('id') == 'attribution_url') {
                        const inputElement = clone.querySelector('input');
                        if (inputElement) {
                            inputElement.setAttribute('value', `${window.location.pathname}`); 

                        }
                    } else if(template.getAttribute('id') == 'attribution_section_id') {
                        const inputElement = clone.querySelector('input');
                        if (inputElement) {
                            inputElement.setAttribute('value', `${this.trackingId}`); 

                        }
                    } else if (this.trackingId === 'cart-recommendations' && template.getAttribute('id') == 'cart-recommendation-for') {
                        const inputElement = clone.querySelector('input');
                        if (inputElement) {
                            inputElement.setAttribute('value', `${this.cartRecommendationFor}`);
                        }
                    }
                    
                    form.append(clone);
                });
            });
        }
    }

    show() {
        this.fetchProduct(this.innerToggle ? true : false);
        document.body.classList.add('modal-open');
        this.setAttribute('data-align', this.align);
        if (this.innerToggle) {
            this.innerToggle.classList.add('working');
            // this.innerToggle = null;
        } else {
            this.loader.classList.add('working');
        }

        this.loader.querySelector('.button__preloader-text').textContent = `Loading your next pair of ${this.title}...`;
        this.classList.add('active');
        this.setAttribute('aria-hidden', 'false');
    }

    hide() {
        this.classList.remove('active');
        this.classList.add('closing');
        this.setAttribute('aria-hidden', 'true');
        setTimeout(() => {
            this.setAttribute('data-align', '');
            this.classList.remove('closing');
            const content = this.querySelectorAll('some-selector'); // Define proper selector
            if (content && content.length) {
                content.forEach((el) => {
                    el.remove();
                });
            }
            this.url = null; 

            document.body.classList.remove('modal-open');
            this.classList.remove('active'); 
            this.classList.contains('loaded') && this.classList.remove('loaded');
        }, 300);
    }

    loaded() {
        this.loader.classList.remove('working');

        if(!this.classList.contains('loaded')) {
            this.classList.add('loaded')
        }

        if(this.classList.contains('working')) {
            this.classList.remove('working')
            this.loader.classList.remove('working');
        }
        
        if(this.classList.contains('inner-working')) {
            this.classList.remove('inner-working')
        }
    }

    error(message) {
        console.error('ProductModal error:', message);
        // Handle error display
    }
}

customElements.define('product-modal', ProductModal);

class ProductModalToggle extends HTMLElement {
    constructor() {
        super();

        this.addEventListener('click', (e) => {
            e.preventDefault();
            const modal = document.querySelector('product-modal');
            modal.init(this);
        });
    }
}

customElements.define('product-modal-toggle', ProductModalToggle);

class PmdLoader extends HTMLElement {

}

class PmdContent extends HTMLElement {

}

class PmdClose extends HTMLElement {
    
}

customElements.define('pmd-loader', PmdLoader);
customElements.define('pmd-content', PmdContent);
customElements.define('pmd-close', PmdClose);

class SetModalToggle extends HTMLElement {
    constructor() {
        super();

        this.addEventListener('click', (e) => {
            e.preventDefault();
            const modal = document.querySelector('product-modal');
            modal.init(this);
        });
    }
}

customElements.define('set-modal-toggle', SetModalToggle);

class InnerProductModalToggle extends HTMLElement {
    constructor() {
        super();

        this.addEventListener('click', (e) => {
            e.preventDefault();
            this.classList.add('working');
            const modal = document.querySelector('product-modal');
            modal.init(this);
        });
    }
}

customElements.get('inner-product-modal-toggle') || customElements.define('inner-product-modal-toggle', InnerProductModalToggle);


if (!window._formatMoney || typeof window._formatMoney !== 'function') {
window._formatMoney = function(cents, format) {
    if (typeof cents == 'string') { cents = cents.replace('.',''); }
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = (format || this.money_format);
  
    function defaultOption(opt, def) {
       return (typeof opt == 'undefined' ? def : opt);
    }
  
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal   = defaultOption(decimal, '.');
  
      if (isNaN(number) || number == null) { return 0; }
  
      number = (number/100.0).toFixed(precision);
  
      var parts   = number.split('.'),
          dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
          cents   = parts[1] ? (decimal + parts[1]) : '';
  
      return dollars + cents;
    }
  
    switch(formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }
  
    return formatString.replace(placeholderRegex, value);
  };
}

if (!window._serializeForm || typeof window._serializeForm !== 'function') {

window._serializeForm = function(form) {
    let arr = [];
    Array.prototype.slice.call(form.elements).forEach(function(field) {
        if (
            !field.name ||
            field.disabled ||
            ['file', 'reset', 'submit', 'button'].indexOf(field.type) > -1
        )
            return;
        if (field.type === 'select-multiple') {
            Array.prototype.slice.call(field.options).forEach(function(option) {
                if (!option.selected) return;
                arr.push(
                    encodeURIComponent(field.name) +
                        '=' +
                        encodeURIComponent(option.value)
                );
            });
            return;
        }
        if (['checkbox', 'radio'].indexOf(field.type) > -1 && !field.checked)
            return;
        arr.push(
            encodeURIComponent(field.name) + '=' + encodeURIComponent(field.value)
        );
    });
    return arr.join('&');
};
}